import { useCallback } from "react"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { ElementRenderer } from "./element-renderer"
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../components/ui/table"
import { cn } from "../lib/utils"

export function TableRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent } = useRenderContext()
  // Theme-v2 atom defaults feed the fallback chain:
  //   element.variant/size → atomDefaults.default* → shadcn "default"
  // Note: `element.variant === "striped"` is a legacy row-banding flag used
  // by the body row className below; it coexists with the theme-variant
  // data-* surface. The raw token is preserved on data-variant.
  const atomDefaults = useAtomDefaults("table")
  const resolvedVariant = (element.variant as string | undefined)
    ?? atomDefaults.defaultVariant
    ?? "default"
  const resolvedSize = (element as { size?: string }).size
    ?? atomDefaults.defaultSize
    ?? "default"
  const isStriped = element.variant === "striped"

  const handleRowClick = useCallback(
    (rowIndex: number) => {
      dispatchEvent(element.id, "rowClick", { rowIndex })
    },
    [element.id, dispatchEvent],
  )

  const children = element.children ?? []

  // Support both flat (old: [headerRow, ...dataRows]) and semantic (new: [table-header, table-body])
  let headerRow: typeof children[0] | undefined
  let dataRows: typeof children

  const firstChild = children[0]
  if (firstChild?.type === "table-header") {
    // Semantic structure: table → table-header → [row], table-body → [row, ...]
    headerRow = firstChild.children?.[0]
    const bodyNode = children.find((c) => c.type === "table-body")
    dataRows = bodyNode?.children ?? []
  } else {
    // Flat structure: table → [headerRow, ...dataRows]
    headerRow = firstChild
    dataRows = children.slice(1)
  }

  // Collect header texts for data-column attributes on body cells
  const headerTexts = headerRow?.children?.map((cell) =>
    cell.text ? String(resolveSymbol(cell.text) ?? "") : "",
  ) ?? []

  return (
    <div
      className={cn("cn-table-container", element.className)}
      data-testid={`table-${element.id}`}
      data-atom-type="table"
      data-variant={resolvedVariant}
      data-size={resolvedSize}
    >
      <Table>
        {headerRow && (
          <TableHeader>
            <TableRow>
              {headerRow.children?.map((cell) => {
                const text = cell.text
                  ? String(resolveSymbol(cell.text) ?? "")
                  : undefined

                return (
                  <TableHead
                    key={cell.id}
                    className={cell.className}
                    data-column={text ?? ""}
                  >
                    {text ?? (
                      cell.children?.map((child) => (
                        <ElementRenderer key={child.id} element={child} />
                      ))
                    )}
                  </TableHead>
                )
              })}
            </TableRow>
          </TableHeader>
        )}
        <TableBody>
          {dataRows.map((row, rowIndex) => (
            <TableRow
              key={`${row.id}-${rowIndex}`}
              className={cn(
                "cursor-pointer",
                isStriped && rowIndex % 2 === 1 && "bg-muted/50",
              )}
              onClick={() => handleRowClick(rowIndex)}
            >
              {row.children?.map((cell, cellIndex) => {
                const text = cell.text
                  ? String(resolveSymbol(cell.text) ?? "")
                  : undefined

                return (
                  <TableCell
                    key={cell.id}
                    className={cell.className}
                    data-column={headerTexts[cellIndex] ?? ""}
                    data-row-index={rowIndex}
                  >
                    {text ?? (
                      cell.children?.map((child) => (
                        <ElementRenderer key={child.id} element={child} />
                      ))
                    )}
                  </TableCell>
                )
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
